import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type { Basis, Granularity } from '@progear/finance-core';
import { emptyFilters, type CashFlowFilters } from '@/lib/cashFlowFilters';
import { yearRange, type CashFlowRange, type MoneyMode } from '@/lib/cashFlow';
import type { LedgerScope } from './Ledger';
import type { DaySort } from './CalendarGrid';

/**
 * Module 1.1 — the ONE workspace state for the cash-flow primary view. Every lens
 * (calendar, table, the universal register) and any future KPI lens reads the SAME
 * money-mode, faceted filter, period/window/granularity, scope, selection and
 * "show only selected" from here — never its own copy (replit.md L14: the lenses
 * share every function + state; a capability living in one lens is a defect). The
 * provider owns the raw state + actions; the lenses consume it through
 * `useCashFlowWorkspace`. It holds no money math (L9) — only selection + view state.
 */
export type CashFlowViewKind = 'table' | 'calendar';

/** How the shared search box acts: 'filter' narrows EVERY lens to the matches (the
 *  faceted `filters.search`); 'locate' keeps the current view and instead reveals
 *  (scroll + flash) the matching document in place. */
export type SearchBehavior = 'filter' | 'locate';

/** The prepayment scope of the CASH lens: 'all' shows everything; 'certain' hides
 *  katteta ettemaksud (finance-core `uncertainPrepayment`) from every consumer. */
export type PrepaymentScope = 'all' | 'certain';

/** Prognoos: Väljas / Koos / Lahus — how EVERY lens treats plan (forecast) money:
 *  'excluded' drops still-pending forecast documents (finance-core `forecastPending`)
 *  from every consumer; 'blended' shows one merged number (no split); 'split' (the
 *  default) keeps the blend AND surfaces the forecast share as the amber "sh
 *  prognoos" sub-lines + the confirmed balance twin. One shared truth (L14). */
export type ForecastScope = 'excluded' | 'blended' | 'split';

/** A locate request — the document to reveal + a nonce so re-selecting the SAME
 *  document still re-fires the scroll/flash (the value changes on every locate). */
export interface ActiveMatch {
  entryId: string;
  nonce: number;
}

export interface CashFlowWorkspaceValue {
  /** NET / GROSS display mode — changes presentation only, never a calc (L9). */
  mode: MoneyMode;
  setMode: Dispatch<SetStateAction<MoneyMode>>;
  /** Rahavoog / Tekkepõhine — the reporting basis EVERY lens resolves placements
   *  under (finance-core `Basis`). Cash (default) is the treasury calendar;
   *  accrual rests each document on its issue date so the window reconciles with
   *  an accrual report. One shared truth, exactly like `mode` (L14). */
  basis: Basis;
  setBasis: Dispatch<SetStateAction<Basis>>;
  /** Kõik / Kindlad — the shared prepayment scope of the CASH lens. `certain` drops
   *  katteta ettemaksud (unpaid, unlinked income prepayment invoices — pure hope)
   *  from EVERY consumer via finance-core's `uncertainPrepayment`, so the forecast
   *  shows only money that is received or anchored to a real downstream document.
   *  Inert under the accrual basis (accrual already drops ALL prepayments). Session
   *  local, never persisted — the calendar always opens showing everything (L14). */
  prepaymentScope: PrepaymentScope;
  setPrepaymentScope: Dispatch<SetStateAction<PrepaymentScope>>;
  /** "Rõhuta prognoos" — when on, EVERY lens (calendar chip, register row, matrix
   *  forecast column) washes forecast entries in the amber --pending cue. Shared
   *  toolbar toggle, one truth (L14 — an enhancement targets the shared layer). */
  forecastEmphasis: boolean;
  setForecastEmphasis: Dispatch<SetStateAction<boolean>>;
  /** Prognoos: Väljas / Koos / Lahus — the shared plan-money scope of BOTH lenses
   *  (see {@link ForecastScope}). Session-local, never persisted; defaults to
   *  'split' so the year opens showing the confirmed/plan breakdown. */
  forecastScope: ForecastScope;
  setForecastScope: Dispatch<SetStateAction<ForecastScope>>;
  /** Päev / Nädal / Kuu / Kvartal — the shared period grain of both lenses. */
  granularity: Granularity;
  setGranularity: Dispatch<SetStateAction<Granularity>>;
  /** Which primary lens is on screen (table vs calendar). */
  view: CashFlowViewKind;
  setView: Dispatch<SetStateAction<CashFlowViewKind>>;
  /** The register overlay: `undefined` closed · `null` all documents · a scope = drill. */
  ledgerScope: LedgerScope | null | undefined;
  setLedgerScope: Dispatch<SetStateAction<LedgerScope | null | undefined>>;
  /** The enriched-entry / document overlay (module 1.3): the id of the entry whose
   *  detail is open, or `null` when closed. ONE shared truth so BOTH lenses open the
   *  same drill surface identically (L14) — never a lens-local detail state. */
  selectedEntryDetailId: string | null;
  /** Open the enriched detail for `id` (used by every lens' direct-open affordance). */
  openEntryDetail: (id: string) => void;
  /** Close the enriched detail overlay. */
  closeEntryDetail: () => void;
  /** The faceted filter — one selection narrows EVERY lens identically. */
  filters: CashFlowFilters;
  setFilters: Dispatch<SetStateAction<CashFlowFilters>>;
  /** Calendar day-internal ordering (a presentation choice of the calendar lens). */
  daySort: DaySort;
  setDaySort: Dispatch<SetStateAction<DaySort>>;
  /** Bumped by "Täna" so BOTH lenses re-centre on today (shared chrome). */
  todayNonce: number;
  bumpToday: () => void;
  /** The period window the shared query layer fetches; `undefined` = the default year. */
  windowRange: CashFlowRange | undefined;
  setWindowRange: Dispatch<SetStateAction<CashFlowRange | undefined>>;
  /** The year the period navigator steps through. */
  year: number;
  setYear: Dispatch<SetStateAction<number>>;
  /** Estonian label for the active period (null = the bare year). */
  periodText: string | null;
  setPeriodText: Dispatch<SetStateAction<string | null>>;
  /** Cross-lens selection — the document ids the user has ticked in the register. */
  selectedIds: string[];
  selectedIdSet: ReadonlySet<string>;
  toggleSelected: (id: string) => void;
  setSelected: Dispatch<SetStateAction<string[]>>;
  clearSelection: () => void;
  /** "Ainult valitud" — when on, every lens shows only the selected documents. */
  selectedOnly: boolean;
  setSelectedOnly: Dispatch<SetStateAction<boolean>>;
  /** How the register search box behaves — 'filter' (narrow every lens) vs 'locate'
   *  (keep the view, reveal the match in place). Shared so the choice is one truth. */
  searchBehavior: SearchBehavior;
  setSearchBehavior: Dispatch<SetStateAction<SearchBehavior>>;
  /** The document a "locate" asked every lens to reveal (or null). Read by the shared
   *  useLocateScroll hook in each lens — never a lens-local highlight (L14). */
  activeMatch: ActiveMatch | null;
  /** Ask every lens to reveal `entryId` (scroll into view + flash). Bumps the nonce so
   *  re-locating the same document re-fires. */
  locateEntry: (entryId: string) => void;
}

const CashFlowWorkspaceContext = createContext<CashFlowWorkspaceValue | null>(null);

/** The year a window opens on: the range's start year, else the current year. */
export function initialYear(range?: CashFlowRange): number {
  if (range?.from) return Number(range.from.slice(0, 4));
  return new Date().getUTCFullYear();
}

export interface CashFlowWorkspaceProviderProps {
  /** A parent-driven window (deep-link / external nav); internal nav owns it after. */
  range?: CashFlowRange;
  children: ReactNode;
}

export function CashFlowWorkspaceProvider({ range, children }: CashFlowWorkspaceProviderProps) {
  const [mode, setMode] = useState<MoneyMode>('gross');
  // The treasury (cash) lens is the primary view's identity — always the default;
  // the accrual lens is an explicit, session-local choice (never persisted).
  const [basis, setBasis] = useState<Basis>('cashBasis');
  // Everything visible by default — hiding katteta ettemaksud is an explicit,
  // session-local choice, exactly like the accrual lens above.
  const [prepaymentScope, setPrepaymentScope] = useState<PrepaymentScope>('all');
  // Off by default — the year opens calm; the user opts into the forecast wash.
  const [forecastEmphasis, setForecastEmphasis] = useState(false);
  // 'split' by default — the matrix opens with the "sh prognoos" breakdown visible.
  const [forecastScope, setForecastScope] = useState<ForecastScope>('split');
  // Default to the month (year-at-a-glance) view so the first load is the whole year.
  const [granularity, setGranularity] = useState<Granularity>('month');
  const [view, setView] = useState<CashFlowViewKind>('table');
  const [ledgerScope, setLedgerScope] = useState<LedgerScope | null | undefined>(undefined);
  const [selectedEntryDetailId, setSelectedEntryDetailId] = useState<string | null>(null);
  const [filters, setFilters] = useState<CashFlowFilters>(emptyFilters);
  const [daySort, setDaySort] = useState<DaySort>('date');
  const [todayNonce, setTodayNonce] = useState(0);
  const [windowRange, setWindowRange] = useState<CashFlowRange | undefined>(
    () => range ?? yearRange(initialYear(range)),
  );
  const [year, setYear] = useState<number>(() => initialYear(range));
  const [periodText, setPeriodText] = useState<string | null>(null);
  const [selectedIds, setSelected] = useState<string[]>([]);
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [searchBehavior, setSearchBehavior] = useState<SearchBehavior>('filter');
  const [activeMatch, setActiveMatch] = useState<ActiveMatch | null>(null);

  // Follow a parent-driven window; internal navigation owns it between prop updates.
  useEffect(() => {
    setWindowRange(range ?? yearRange(initialYear(range)));
    setYear(initialYear(range));
    setPeriodText(null);
  }, [range]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const bumpToday = useCallback(() => setTodayNonce((n) => n + 1), []);
  const toggleSelected = useCallback(
    (id: string) =>
      setSelected((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id])),
    [],
  );
  const clearSelection = useCallback(() => setSelected([]), []);
  const openEntryDetail = useCallback((id: string) => setSelectedEntryDetailId(id), []);
  const closeEntryDetail = useCallback(() => setSelectedEntryDetailId(null), []);
  // Bump the nonce so re-locating the SAME document still re-fires the scroll/flash.
  const locateEntry = useCallback(
    (entryId: string) => setActiveMatch((m) => ({ entryId, nonce: (m?.nonce ?? 0) + 1 })),
    [],
  );

  const value = useMemo<CashFlowWorkspaceValue>(
    () => ({
      mode,
      setMode,
      basis,
      setBasis,
      prepaymentScope,
      setPrepaymentScope,
      forecastEmphasis,
      setForecastEmphasis,
      forecastScope,
      setForecastScope,
      granularity,
      setGranularity,
      view,
      setView,
      ledgerScope,
      setLedgerScope,
      selectedEntryDetailId,
      openEntryDetail,
      closeEntryDetail,
      filters,
      setFilters,
      daySort,
      setDaySort,
      todayNonce,
      bumpToday,
      windowRange,
      setWindowRange,
      year,
      setYear,
      periodText,
      setPeriodText,
      selectedIds,
      selectedIdSet,
      toggleSelected,
      setSelected,
      clearSelection,
      selectedOnly,
      setSelectedOnly,
      searchBehavior,
      setSearchBehavior,
      activeMatch,
      locateEntry,
    }),
    [
      mode,
      basis,
      prepaymentScope,
      forecastEmphasis,
      forecastScope,
      granularity,
      view,
      ledgerScope,
      selectedEntryDetailId,
      openEntryDetail,
      closeEntryDetail,
      filters,
      daySort,
      todayNonce,
      bumpToday,
      windowRange,
      year,
      periodText,
      selectedIds,
      selectedIdSet,
      toggleSelected,
      clearSelection,
      selectedOnly,
      searchBehavior,
      activeMatch,
      locateEntry,
    ],
  );

  return <CashFlowWorkspaceContext.Provider value={value}>{children}</CashFlowWorkspaceContext.Provider>;
}

/** Read the shared workspace; throws if rendered outside the provider. */
export function useCashFlowWorkspace(): CashFlowWorkspaceValue {
  const value = useContext(CashFlowWorkspaceContext);
  if (!value) throw new Error('useCashFlowWorkspace must be used within a CashFlowWorkspaceProvider');
  return value;
}
